<template>
  <van-popup
    v-model="dialogVisible"
    class="avatar-picker-popup"
    round
    closeable
    close-icon="cross"
    position="bottom"
  >
    <div class="avatar-picker">
      <h3>{{ $t('avatar.title') }}</h3>
      <p class="section-title">
        {{ $t('avatar.preset') }}
      </p>
      <div
        v-if="options.length"
        class="avatar-grid"
      >
        <button
          v-for="item in options"
          :key="item.id"
          type="button"
          class="avatar-option"
          :class="{ selected: item.icon === currentAvatar }"
          :disabled="saving"
          @click="selectAvatar(item)"
        >
          <img
            :src="item.icon"
            alt=""
          >
        </button>
      </div>
      <p
        v-else-if="!optionsLoading"
        class="empty-tip"
      >
        {{ $t('avatar.empty') }}
      </p>

      <p class="section-title upload-title">
        {{ $t('avatar.upload') }}
      </p>
      <van-uploader
        v-model="fileList"
        :max-count="1"
        :max-size="2097152"
        :before-read="beforeRead"
        :after-read="afterRead"
        :disabled="saving"
        @oversize="onOversize"
        @delete="onDelete"
      >
        <div class="upload-button">
          <van-icon name="photograph" />
          <span>{{ saving ? $t('avatar.uploading') : $t('avatar.upload') }}</span>
        </div>
      </van-uploader>
    </div>
  </van-popup>
</template>

<script>
import Vue from 'vue';
import axios from 'axios';
import Api from '../interface/index';
import Fetch from '../utils/fetch';
import { Icon, Popup, Toast, Uploader } from 'vant';

Vue.use(Icon).use(Popup).use(Toast).use(Uploader);

export default {
  name: 'AvatarPicker',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    currentAvatar: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      options: [],
      optionsLoading: false,
      saving: false,
      fileList: [],
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
  },
  watch: {
    value(visible) {
      if (visible) {
        this.loadOptions();
      }
    },
  },
  methods: {
    loadOptions() {
      if (this.optionsLoading) {
        return;
      }
      this.optionsLoading = true;
      Fetch('/user/avatar_options').then((response) => {
        this.options = response.data && Array.isArray(response.data.avatars) ? response.data.avatars : [];
      }).catch(() => {}).then(() => {
        this.optionsLoading = false;
      });
    },
    selectAvatar(item) {
      if (this.saving || !item || !item.id) {
        return;
      }
      this.saving = true;
      Fetch('/user/avatar_select', { avatar_id: item.id }).then((response) => {
        var icon = response.data && response.data.icon ? response.data.icon : item.icon;
        this.finishUpdate(icon, response.info);
      }).catch(() => {}).then(() => {
        this.saving = false;
      });
    },
    beforeRead(file) {
      var type = String(file.type || '').toLowerCase();
      var name = String(file.name || '').toLowerCase();
      if (!/\.(jpg|jpeg|png)$/.test(name) || ['image/jpeg', 'image/png'].indexOf(type) === -1) {
        Toast(this.$t('avatar.typeError'));
        return false;
      }
      if (file.size > 2 * 1024 * 1024) {
        Toast(this.$t('avatar.sizeError'));
        return false;
      }
      return true;
    },
    afterRead(file) {
      if (this.saving || !file || !file.file) {
        return;
      }
      var formData = new FormData();
      formData.append('file', file.file);
      formData.append('language', localStorage.getItem('lang') || 'en_us');
      formData.append('token', localStorage.getItem('token') || '');
      this.saving = true;
      axios.post(Api.commonUrl + '/api/user/avatar_upload', formData).then((result) => {
        var response = result.data || {};
        if (Number(response.code) !== 1) {
          Toast(this.responseMessage(response.info, this.$t('avatar.saveFail')));
          return;
        }
        var icon = response.data && response.data.icon ? response.data.icon : '';
        this.finishUpdate(icon, response.info);
      }).catch(() => {
        Toast(this.$t('avatar.saveFail'));
      }).then(() => {
        this.saving = false;
      });
    },
    onOversize() {
      Toast(this.$t('avatar.sizeError'));
    },
    onDelete() {
      this.fileList = [];
    },
    finishUpdate(icon, message) {
      if (!icon) {
        Toast(this.$t('avatar.saveFail'));
        return;
      }
      this.$emit('updated', icon);
      this.fileList = [];
      this.dialogVisible = false;
      Toast(this.responseMessage(message, this.$t('avatar.success')));
    },
    responseMessage(info, fallback) {
      var language = localStorage.getItem('lang') || 'en_us';
      if (info && typeof info === 'object') {
        return info[language] || info.en_us || info.zh_cn || fallback;
      }
      return info || fallback;
    },
  },
};
</script>

<style scoped>
.avatar-picker {
  padding: 22px 18px 28px;
  color: #061429;
}

.avatar-picker h3 {
  margin: 0 0 18px;
  text-align: center;
  font-size: 16px;
}

.section-title {
  margin: 0 0 10px;
  color: #687083;
  font-size: 12px;
}

.avatar-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
}

.avatar-option {
  width: 100%;
  padding: 2px;
  border: 2px solid transparent;
  border-radius: 50%;
  background: transparent;
}

.avatar-option.selected {
  border-color: #ff4551;
}

.avatar-option img {
  display: block;
  width: 100%;
  aspect-ratio: 1;
  border-radius: 50%;
  object-fit: cover;
}

.empty-tip {
  margin: 6px 0 0;
  color: #8b93a3;
  font-size: 12px;
}

.upload-title {
  margin-top: 20px;
}

.upload-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 132px;
  height: 38px;
  border-radius: 19px;
  background: #f2f4fa;
  color: #337cf4;
  font-size: 12px;
  gap: 6px;
}

.upload-button .van-icon {
  font-size: 16px;
}

.avatar-picker /deep/ .van-uploader__preview,
.avatar-picker /deep/ .van-uploader__preview-image {
  width: 54px;
  height: 54px;
}
</style>
